/**
 * ponArt — the Pon Pon character's **visual atoms**, factored out so BOTH the
 * DOM plaza (`ui/blob.ts#ponBlobHtml`) and the canvas stages
 * (`ui/character/ponCharacter.ts`) draw the SAME gumdrop from ONE source of
 * truth. Verbatim from the prototype `docs/design/flow/ui.jsx` (the squishy
 * gumdrop body path, the `shade` costume-tint ramp, eyes, mouth, costumes).
 *
 * Pure string/colour helpers — no DOM, no Phaser. `ponBlobHtml` re-imports
 * these so the plaza rendering stays byte-identical; the canvas character
 * rasterises {@link ponBodySvg} to a texture so the stage Pon is pixel-identical
 * to the plaza Pon.
 */

import type { CostumeId } from '@ponponland/shared';

/** Pon outline colour (prototype `F`). */
export const PON_OUTLINE = '#21306b';

/** Squishy gumdrop body path (verbatim from the prototype). */
export const BODY =
  'M50 13 C68 13 82 27 86 48 C90 69 80 85 50 85 ' +
  'C20 85 10 69 14 48 C18 27 32 13 50 13 Z';

/** Darken/lighten a hex colour by an additive amount (prototype `shade`). */
export function shade(hex: string, amt: number): string {
  const n = parseInt(hex.slice(1), 16);
  let r = (n >> 16) + amt;
  let g = ((n >> 8) & 255) + amt;
  let b = (n & 255) + amt;
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}

export type Eyes = 'happy' | 'open';

/** The "happy" eye set (closed-curve smile eyes) from the prototype. */
export function eyeSet(expr: Eyes): string {
  const F = PON_OUTLINE;
  if (expr === 'happy') {
    return `<g fill="none" stroke="${F}" stroke-width="3.6" stroke-linecap="round"><path d="M33 50 q5 -7 10 0"/><path d="M57 50 q5 -7 10 0"/></g>`;
  }
  // 'open' — round eyes with a highlight
  return `<g><ellipse cx="39" cy="51" rx="4.8" ry="6.2" fill="${F}"/><circle cx="40.8" cy="48.5" r="1.7" fill="#fff"/><ellipse cx="61" cy="51" rx="4.8" ry="6.2" fill="${F}"/><circle cx="62.8" cy="48.5" r="1.7" fill="#fff"/></g>`;
}

/** Smile mouth (default elementless expression). */
export function mouth(): string {
  const F = PON_OUTLINE;
  return `<path d="M45 62 q5 4.5 10 0" fill="none" stroke="${F}" stroke-width="2.8" stroke-linecap="round"/>`;
}

/** Costume accessory atop the blob (subset shipped so far). */
export function costume(id: CostumeId): string {
  const O = PON_OUTLINE;
  switch (id) {
    case 'straw':
      return `<g stroke="${O}" stroke-width="2.4" stroke-linejoin="round"><ellipse cx="50" cy="20" rx="33" ry="9" fill="#e6b762"/><path d="M34 20 C34 8 66 8 66 20 Z" fill="#edc77e"/><path d="M34 18 h32" stroke="#c2913f" stroke-width="3"/></g>`;
    case 'crown':
      return `<g stroke="${O}" stroke-width="2.2" stroke-linejoin="round"><path d="M30 18 L34 6 L42 14 L50 4 L58 14 L66 6 L70 18 Z" fill="#ffd166"/><circle cx="50" cy="9" r="2" fill="#ec6a86"/></g>`;
    case 'bow':
      return `<g stroke="${O}" stroke-width="2.2" stroke-linejoin="round"><path d="M50 14 L36 6 L36 22 Z" fill="#f47e96"/><path d="M50 14 L64 6 L64 22 Z" fill="#f47e96"/><circle cx="50" cy="14" r="5" fill="#ec6a86"/></g>`;
    case 'leaf':
      return `<g stroke="${O}" stroke-width="2.2" stroke-linejoin="round"><path d="M50 16 L50 4" fill="none" stroke-linecap="round"/><path d="M50 9 C42 9 38 3 39 -2 C46 -2 50 4 50 9 Z" fill="#7ec84a"/><path d="M50 12 C58 12 62 6 61 1 C54 1 50 7 50 12 Z" fill="#9ad95f"/></g>`;
    case 'goggles':
      return `<g stroke="${O}" stroke-width="2.2"><path d="M28 30 h44" fill="none" stroke-width="3"/><circle cx="39" cy="31" r="7" fill="#bfe6ff" stroke="${O}" stroke-width="2.4"/><circle cx="61" cy="31" r="7" fill="#bfe6ff" stroke="${O}" stroke-width="2.4"/><circle cx="39" cy="31" r="2.4" fill="#fff" opacity=".8"/></g>`;
    case 'flower': {
      const petals = [0, 72, 144, 216, 288]
        .map((a) => {
          const r = (a * Math.PI) / 180;
          return `<ellipse cx="${33 + Math.cos(r) * 6}" cy="${13 + Math.sin(r) * 6}" rx="4" ry="4" fill="#ff9bd0"/>`;
        })
        .join('');
      return `<g stroke="${O}" stroke-width="1.8" stroke-linejoin="round">${petals}<circle cx="33" cy="13" r="3.4" fill="#ffd166"/></g>`;
    }
    case 'cap':
      return `<g stroke="${O}" stroke-width="2.4" stroke-linejoin="round"><path d="M32 18 C32 6 68 6 68 17 L34 17 Z" fill="#52b39a"/><path d="M66 17 C82 16 84 23 80 24 L66 21 Z" fill="#3f8f78"/><circle cx="50" cy="9" r="2.4" fill="#fdf3d8"/></g>`;
    default:
      return '';
  }
}

export interface PonBodyOptions {
  colour: string;
  costume: CostumeId;
  eyes?: Eyes;
  /**
   * Shift the face (eyes/blush/mouth) this many viewBox px toward the right so
   * the Pon reads as facing a direction. 0 = front-facing (menus).
   */
  faceShift?: number;
  /**
   * The big absorb mouth's visibility:
   * - `'css'` (default): emit it with NO inline opacity — the DOM plaza's CSS
   *   (`.pon-absorb-mouth` / `.pon-absorbing` / `.pon-absorbed`) controls it.
   * - `true` / `false`: bake an explicit inline opacity into the SVG — used by
   *   the canvas character, which has no stylesheet and instead builds separate
   *   textures for the open-mouth (absorbing/absorbed) vs closed-mouth poses.
   */
  absorbMouth?: 'css' | boolean;
}

/**
 * The Pon's body as the inner SVG markup (the `<defs>` + gumdrop + face +
 * costume, WITHOUT the outer `<svg>`/`<div>` wrapper). Shared by `ponBlobHtml`
 * (wraps it for the DOM) and `ponCharacter` (wraps it in a standalone `<svg>` to
 * rasterise to a Phaser texture). The radial-gradient id is derived from the
 * colour so multiple Pons on one page never collide.
 */
export function ponBodyInner({ colour, costume: cos, eyes = 'happy', faceShift = 0, absorbMouth = 'css' }: PonBodyOptions): string {
  const top = shade(colour, 82);
  const mid = shade(colour, 46);
  const low = shade(colour, 8);
  const gid = 'pg' + colour.slice(1).replace(/[^0-9a-f]/gi, '');
  // DOM ('css'): no inline opacity → the stylesheet controls visibility (plaza
  // byte-identical). Canvas (boolean): bake the opacity into the texture.
  const inlineOpacity = absorbMouth === 'css' ? '' : ` style="opacity:${absorbMouth ? 1 : 0}"`;
  const absorbMouthEl = `<ellipse class="pon-absorb-mouth" cx="50" cy="63" rx="9" ry="11" fill="#5a2336" stroke="${PON_OUTLINE}" stroke-width="2.6"${inlineOpacity}/>`;
  const faceInner =
    `<ellipse cx="31" cy="60" rx="6" ry="3.6" fill="#ff9bad" opacity=".7"/>` +
    `<ellipse cx="69" cy="60" rx="6" ry="3.6" fill="#ff9bad" opacity=".7"/>` +
    eyeSet(eyes) +
    mouth() +
    absorbMouthEl;
  const face = faceShift ? `<g transform="translate(${faceShift},0)">${faceInner}</g>` : faceInner;

  return `<defs>
      <radialGradient id="${gid}" cx="40%" cy="26%" r="82%">
        <stop offset="0%" stop-color="#ffffff"/><stop offset="34%" stop-color="${top}"/>
        <stop offset="72%" stop-color="${mid}"/><stop offset="100%" stop-color="${low}"/>
      </radialGradient>
    </defs>
    <ellipse cx="50" cy="90" rx="29" ry="4.5" fill="#1c2b5e" opacity=".18"/>
    <path d="${BODY}" fill="url(#${gid})" stroke="${PON_OUTLINE}" stroke-width="3.2"/>
    <ellipse cx="50" cy="72" rx="30" ry="15" fill="${low}" opacity=".25"/>
    <path d="M32 28 C27 33 25 42 29 49 C35 45 37 35 36 28 C35 24 33 24 32 28 Z" fill="#ffffff" opacity=".7"/>
    <circle cx="64" cy="28" r="4.5" fill="#ffffff" opacity=".5"/>
    <circle cx="71" cy="42" r="2.8" fill="#ffffff" opacity=".4"/>
    <circle cx="44" cy="21" r="2.2" fill="#ffffff" opacity=".6"/>
    ${face}
    ${costume(cos)}`;
}

/**
 * A standalone `<svg>` document for the Pon body — used by the canvas character
 * to build an Image/texture. `size` is the rendered pixel size; the viewBox is
 * the prototype's 100×100, `overflow:visible` so tall costumes aren't clipped.
 */
export function ponBodySvg(opts: PonBodyOptions & { size: number }): string {
  const { size } = opts;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 100 100" style="overflow:visible">${ponBodyInner(opts)}</svg>`;
}
